import { NavLink } from 'react-router';
import { PageHead } from 'ui/kit/PageHead';
import { useLiveCountdown } from 'hooks/useLiveCountdown';

import { useTutorTrustScore, type TutorTrustScoreChecklistItem } from 'hooks/useTutorTrustScore';
import { formatDailyResponsesResetLabel } from 'utils/tutorDailyResponses';

import { getTrustLevelView, TRUST_LEVELS, trustProgressPercent } from '../utils/trustScoreView';
import classes from '../Teacher.module.scss';

const CHECKLIST_GROUP_ORDER = ['profile', 'verification', 'bonus'] as const;

function formatChecklistDelta(delta: number): string {
  const abs = Math.abs(delta);
  const text = Number.isInteger(abs) ? abs.toFixed(0) : abs.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  return delta > 0 ? `+${text}` : text;
}

function groupChecklist(items: TutorTrustScoreChecklistItem[]) {
  const map = new Map<string, { key: string; title: string; items: TutorTrustScoreChecklistItem[] }>();
  for (const item of items) {
    const key = item.group || 'profile';
    const title = item.group_title_ru || (key === 'verification' ? 'Проверка' : key === 'bonus' ? 'Бонус' : 'Профиль');
    if (!map.has(key)) {
      map.set(key, { key, title, items: [] });
    }
    map.get(key)!.items.push(item);
  }
  return CHECKLIST_GROUP_ORDER.map((key) => map.get(key)).filter(Boolean) as Array<{
    key: string;
    title: string;
    items: TutorTrustScoreChecklistItem[];
  }>;
}

const EVENT_LABELS: Record<string, string> = {
  profile_complete: 'Бонус за полностью собранный профиль',
  contacts_verified: 'Подтверждены email и телефон',
  docs_verified: 'Подтвержден паспорт',
  review_5_star: 'Одобренный отзыв 5',
  review_rated: 'Одобренный отзыв',
  review_4_star: 'Одобренный отзыв (4 и выше)',
  order_completed: 'Завершенная сделка',
  order_completed_cheap: 'Завершенная сделка (базовая)',
  order_completed_medium: 'Завершенная сделка (средняя)',
  order_completed_premium: 'Завершенная сделка (премиальная)',
  profile_fields_approved: 'Одобрено поле профиля',
  profile_data_updated: 'Обновление профиля',
  daily_task_complete: 'Выполнено дневное задание',
  admin_adjustment: 'Корректировка модератора',
  commission_refused: 'Отказ от комиссии',
  student_declined_after_accept: 'Отказ ученика после принятия отклика',
  inactivity_14d: 'Длительная неактивность',
  complaint_confirmed: 'Подтвержденная жалоба',
};

function eventLabel(event: {
  event_type: string;
  reason_ru?: string | null;
  meta?: Record<string, unknown> | null;
}): string {
  const fromApi = (event.reason_ru || '').trim();
  if (fromApi && fromApi !== event.event_type && !/^[a-z][a-z0-9_]*$/i.test(fromApi)) {
    return fromApi.charAt(0).toUpperCase() + fromApi.slice(1);
  }

  const meta = event.meta || {};
  const field = typeof meta.field === 'string' ? meta.field : '';
  if (event.event_type === 'profile_fields_approved' && field) {
    const fieldLabels: Record<string, string> = {
      name: 'имя',
      surname: 'фамилия',
      patronymic: 'отчество',
      about: 'описание профиля',
      teacher_educations: 'образование',
      teacher_prices: 'услуги и цены',
      profile_photo: 'фото профиля',
    };
    const label = fieldLabels[field] || field;
    if (field === 'about') return 'Одобрено описание профиля (от 200 символов)';
    if (field === 'profile_photo') return 'Одобрено фото профиля';
    if (field === 'teacher_prices') return 'Одобрены услуги и цены';
    if (field === 'teacher_educations') return 'Одобрено образование';
    return `Одобрено поле профиля: ${label}`;
  }
  if (
    (event.event_type === 'review_rated' ||
      event.event_type === 'review_4_star' ||
      event.event_type === 'review_5_star') &&
    meta.rating != null
  ) {
    return `Одобренный отзыв ${Number(meta.rating)}`;
  }

  return EVENT_LABELS[event.event_type] ?? EVENT_LABELS[event.event_type.split(':')[0]] ?? 'Событие рейтинга';
}

const QUEST_STATUS_LABELS: Record<string, string> = {
  available: 'Доступно',
  in_progress: 'В процессе',
  completed: 'Выполнено',
  expired: 'Истекло',
};

const TIER_LABELS: Record<string, string> = {
  cheap: 'Базовые',
  medium: 'Средние',
  premium: 'Премиальные',
};

const levelCards = [
  {
    key: 'bronze',
    score: '0.0+',
    daily: '5 откликов в день',
    tiers: 'только базовые заявки',
    text: 'Стартовый уровень. Можно откликаться на недорогие и короткие заявки.',
    requirement: 'Выдается автоматически при регистрации.',
  },
  {
    key: 'silver',
    score: '2.0+',
    daily: '10 откликов в день',
    tiers: 'базовые + средние заявки',
    text: 'Открывает средние заявки и увеличивает дневной лимит откликов.',
    requirement: 'Подтвердить паспорт и набрать от 2.0 баллов.',
  },
  {
    key: 'gold',
    score: '4.0+',
    daily: '15 откликов в день',
    tiers: 'все типы заявок',
    text: 'Доступ к премиальным заявкам и повышенный приоритет в распределении.',
    requirement: 'Подтвержденный паспорт и рейтинг от 4.0.',
  },
  {
    key: 'platinum',
    score: '4.5+',
    daily: '20 откликов в день',
    tiers: 'все типы заявок',
    text: 'Максимальный приоритет и самый высокий дневной лимит откликов.',
    requirement: 'Подтвержденный паспорт и рейтинг от 4.5.',
  },
] as const;

const bulletinTierCards = [
  {
    key: 'cheap',
    title: 'Базовые заявки',
    examples: 'Бюджет до 1500 руб. за занятие или курс до 2 уроков.',
    who: 'Доступны с уровня "Бронза".',
  },
  {
    key: 'medium',
    title: 'Средние заявки',
    examples: 'Обычные заявки: цена и длительность между базовыми и премиальными.',
    who: 'С уровня "Серебро" (нужен подтвержденный паспорт).',
  },
  {
    key: 'premium',
    title: 'Премиальные заявки',
    examples: 'От 3000 руб. за занятие и курс от 8 занятий.',
    who: 'С уровня "Золото".',
  },
];

const growthTips = [
  'Заполните профиль, услуги и цены - это дает первые баллы.',
  'Подтвердите телефон и e-mail.',
  'Загрузите паспорт (3 фото) и дождитесь одобрения - без этого уровень "Серебро" недоступен.',
  'Завершайте занятия через платформу и получайте отзывы от учеников.',
  'Избегайте подтвержденных жалоб и невыполненных договоренностей.',
];

export const TeacherTrustScorePage = () => {
  const { data, isLoading, isError } = useTutorTrustScore();
  const dailyResponses = data?.daily_responses;
  const countdown = useLiveCountdown(dailyResponses?.resets_at);

  if (isLoading) {
    return (
      <main className={`${classes['teacher-main']} ${classes['trust-page']}`}>
        <p>Загружаем рейтинг...</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className={`${classes['teacher-main']} ${classes['trust-page']}`}>
        <PageHead crumb='Личный кабинет / Рейтинг' title='Рейтинг репетитора' />
        <p>Не удалось загрузить рейтинг. Попробуйте обновить страницу позже.</p>
      </main>
    );
  }

  const currentLevel = getTrustLevelView(data.trust_level || 'bronze');
  const progressPercent = trustProgressPercent(data.progress?.progress);
  const nextLevel = data.progress?.next_level
    ? getTrustLevelView(data.progress.next_level)
    : null;
  const perks = data.perks ?? {};
  const allowedTiers = perks.allowed_tiers ?? [];
  const trustScore = Number.isFinite(data.trust_score) ? data.trust_score : 0;
  const passportVerified = data.passport_verified ?? false;
  const photoPresent = data.profile_photo_present ?? false;
  const capReasons = data.level_cap_reasons_ru ?? [];
  const checklist = data.onboarding_checklist ?? [];
  const checklistGroups = groupChecklist(checklist);
  const checklistEarned = checklist
    .filter((item) => item.awarded)
    .reduce((sum, item) => sum + (item.delta || 0), 0);
  const checklistTotal = checklist.reduce((sum, item) => sum + Math.max(item.delta || 0, 0), 0);
  const checklistProgressPct =
    checklistTotal > 0 ? Math.min(100, Math.round((checklistEarned / checklistTotal) * 100)) : 0;
  const checklistDoneCount = checklist.filter((item) => item.awarded).length;
  const nextChecklistStep = checklist.find(
    (item) =>
      !item.awarded &&
      (item.status === 'missing' || item.status === 'pending_moderation' || item.status === 'ready'),
  );
  const storedLevel = data.stored_trust_level
    ? getTrustLevelView(data.stored_trust_level)
    : null;
  const levelCapped =
    (capReasons.length > 0 || !passportVerified || !photoPresent) &&
    storedLevel &&
    ['silver', 'gold', 'platinum'].includes(data.stored_trust_level ?? '');
  return (
    <main className={`${classes['teacher-main']} ${classes['trust-page']}`}>
      <PageHead crumb='Личный кабинет / Рейтинг' title='Рейтинг репетитора' />
      <section className={classes['trust-page__hero']}>
        <div>
          <p>
            Рейтинг от 0 до 5 показывает надежность профиля. Он влияет на лимит откликов в день,
            доступные типы заявок и приоритет в ленте. Каждый новый репетитор начинает с уровня
            "Бронза". Для перехода на "Серебро" и выше нужны подтвержденный паспорт и фото профиля.
          </p>
        </div>

        {/* Определяем тип карточки по label или key */}
        {(() => {
          const getCardType = () => {
            const label = currentLevel.label?.toLowerCase() || '';
            if (label.includes('бронза') || currentLevel.key === 'bronze') return 'bronze';
            if (label.includes('серебряный') || currentLevel.key === 'silver') return 'silver';
            if (label.includes('золото') || currentLevel.key === 'gold') return 'gold';
            if (label.includes('платиновый') || currentLevel.key === 'platinum') return 'platinum';
            return 'bronze';
          };

          const cardType = getCardType();

          return (
            <div
              className={`${classes['trust-page__level-card']} ${classes[`trust-page__level-card--${cardType}`]}`}
              style={{ ['--trust-color' as string]: currentLevel.color }}
            >
              <span className={classes['trust-page__level-icon']}>
                {currentLevel.shortLabel}
              </span>
              <strong>{trustScore.toFixed(2)} / 5</strong>
              <span>{currentLevel.label} уровень</span>
              {capReasons.length > 0 ? (
                <small className={classes['trust-page__passport-hint']}>
                  {capReasons.join(' - ')} - действует уровень "Бронза"
                </small>
              ) : null}
            </div>
          );
        })()}
      </section>

      {levelCapped ? (
        <section className={classes['trust-page__card']}>
          <h2>Для повышения уровня нужны фото и паспорт</h2>
          <p>
            По баллам у вас уровень "{storedLevel?.label}", но сейчас действуют ограничения уровня
            "Бронза".
            {capReasons.length > 0 ? ` ${capReasons.join('. ')}.` : ''}
          </p>
          <NavLink to='/teacher/edit#photo' className={classes['trust-page__link']}>
            Перейти к заполнению профиля
          </NavLink>
        </section>
      ) : null}

      {checklist.length > 0 ? (
        <section className={classes['trust-page__card']}>
          <div className={classes['trust-page__card-header']}>
            <h2>Как набрать баллы</h2>
            <span>
              {checklistDoneCount}/{checklist.length} - +{checklistEarned.toFixed(2)} из{' '}
              {checklistTotal.toFixed(2)}
            </span>
          </div>

          <div
            className={classes['trust-page__checklist-bar']}
            aria-label={`Прогресс чеклиста ${checklistProgressPct}%`}
          >
            <span style={{ width: `${checklistProgressPct}%` }} />
          </div>

          {nextChecklistStep ? (
            <p className={classes['trust-page__checklist-next']}>
              Следующий шаг:{' '}
              <NavLink to={nextChecklistStep.href || '/teacher/edit'}>
                {nextChecklistStep.title_ru}
              </NavLink>
              {nextChecklistStep.hint_ru ? ` - ${nextChecklistStep.hint_ru}` : null}
            </p>
          ) : (
            <p className={classes['trust-page__checklist-next']}>
              Все пункты онбординга выполнены. Дальше баллы дают сделки, отзывы и задания.
            </p>
          )}

          <div className={classes['trust-page__checklist-groups']}>
            {checklistGroups.map((group) => (
              <div key={group.key} className={classes['trust-page__checklist-group']}>
                <h3>{group.title}</h3>
                <ul className={classes['trust-page__checklist']}>
                  {group.items.map((item) => {
                    const href = item.href || '/teacher/edit';
                    const deltaText = formatChecklistDelta(item.delta);
                    return (
                      <li
                        key={item.code}
                        className={`${classes['trust-page__checklist-item']} ${classes[`trust-page__checklist-item--${item.status}`] || ''}`}
                      >
                        <NavLink to={href} className={classes['trust-page__checklist-link']}>
                          <span
                            className={classes['trust-page__checklist-status']}
                            aria-hidden
                          >
                            {item.awarded ? '' : item.status === 'pending_moderation' ? '...' : '○'}
                          </span>
                          <div>
                            <strong>{item.title_ru}</strong>
                            {item.status_label_ru && !item.awarded ? (
                              <em className={classes['trust-page__checklist-badge']}>
                                {item.status_label_ru}
                              </em>
                            ) : null}
                            {item.hint_ru ? <small>{item.hint_ru}</small> : null}
                          </div>
                          <span className={classes['trust-page__checklist-delta']}>{deltaText}</span>
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

          <NavLink
            to={nextChecklistStep?.href || '/teacher/edit'}
            className={classes['trust-page__link']}
          >
            {nextChecklistStep ? `Перейти: ${nextChecklistStep.title_ru}` : 'Открыть профиль'}
          </NavLink>
        </section>
      ) : null}

      <section className={classes['trust-page__card']}>
        <div className={classes['trust-page__card-header']}>
          <h2>Прогресс</h2>
          <span>{progressPercent}%</span>
        </div>
        <div className={classes['trust-page__bar']} aria-label={`Прогресс ${progressPercent}%`}>
          <span style={{ width: `${progressPercent}%` }} />
        </div>
        <p>
          {nextLevel
            ? `До уровня "${nextLevel.label}" осталось ${data.progress?.remaining ?? 0} балла.`
            : 'Вы достигли максимального уровня по баллам.'}
        </p>
      </section>

      <section className={classes['trust-page__grid']}>
        <div className={classes['trust-page__card']}>
          <h2>Текущие возможности</h2>
          <ul className={classes['trust-page__list']}>
            <li>
              Откликов сегодня:{' '}
              {dailyResponses
                ? `${dailyResponses.responses_used} из ${dailyResponses.responses_max} (осталось ${dailyResponses.responses_remaining})`
                : `${perks.max_daily_responses ?? 0} в день`}
            </li>
            <li>
              Типы заявок:{' '}
              {allowedTiers.length
                ? allowedTiers.map((tier) => TIER_LABELS[tier] ?? tier).join(', ')
                : 'пока недоступны'}
            </li>
            <li>
              Паспорт: {passportVerified ? 'подтвержден' : 'не подтвержден'}
            </li>
          </ul>
          {dailyResponses ? (
            <p className={classes['trust-page__hint']}>
              Лимит обновится в {formatDailyResponsesResetLabel()}
              {countdown ? ` (через ${countdown})` : ''}.
            </p>
          ) : null}
        </div>

        <div className={classes['trust-page__card']}>
          <h2>Как повысить рейтинг</h2>
          <ul className={classes['trust-page__checklist']}>
            {growthTips.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
          <NavLink to='/teacher/edit' className={classes['trust-page__link']}>
            Перейти к заполнению профиля
          </NavLink>
        </div>
      </section>

      <section className={classes['trust-page__card']}>
        <h2>Задания для повышения баллов</h2>
        {data.quests?.length ? (
          <ul className={classes['trust-page__quests']}>
            {data.quests.map((quest) => {
              const expiresLabel = quest.expires_at
                ? new Date(quest.expires_at).toLocaleString('ru-RU', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : null;
              return (
                <li key={quest.code} className={classes[`trust-page__quest--${quest.status}`]}>
                  <div>
                    <strong>{quest.title_ru}</strong>
                    {quest.description_ru ? <p>{quest.description_ru}</p> : null}
                    <small>
                      {QUEST_STATUS_LABELS[quest.status] ?? quest.status}
                      {quest.subject_label ? ` - ${quest.subject_label}` : ''}
                      {expiresLabel ? ` - до ${expiresLabel}` : ''}
                    </small>
                  </div>
                  <div className={classes['trust-page__quest-meta']}>
                    <span>
                      {quest.progress}/{quest.target}
                    </span>
                    <strong>
                      {quest.delta > 0 ? '+' : ''}
                      {quest.delta}
                    </strong>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p>
            Сейчас нет активных заданий. Когда модераторы опубликуют дневные цели, они появятся
            здесь со сроком и прогрессом.
          </p>
        )}
      </section>

      <section className={classes['trust-page__card']}>
        <h2>Типы заявок</h2>
        <p className={classes['trust-page__intro']}>
          Заявки учеников делятся на три категории по бюджету и длительности курса.
          Категория определяется автоматически при публикации заявки.
        </p>
        <div className={classes['trust-page_task__levels']}>
          {bulletinTierCards.map((tier) => (
            <div key={tier.key}>
              <strong>{tier.title}</strong>
              <p>{tier.examples}</p>
              <small>{tier.who}</small>
            </div>
          ))}
        </div>
      </section>

<section className={classes['trust-page__card']}>
  <h2>Уровни доверия</h2>
  <div className={classes['trust-page__levels']}>
    {levelCards.map((level) => {
      const view = TRUST_LEVELS[level.key];
      
      // Определяем тип карточки по ключу
      const getCardType = (key: string) => {
        if (key === 'bronze') return 'bronze';
        if (key === 'silver') return 'silver';
        if (key === 'gold') return 'gold';
        if (key === 'platinum') return 'platinum';
        return '';
      };
      
      const cardType = getCardType(level.key);
      const cardClass = cardType ? `${classes['trust-page__level-card']} ${classes[`trust-page__level-card--${cardType}`]}` : classes['trust-page__level-card'];

    return (
  <div 
    key={level.key} 
    className={cardClass}
    style={{ ['--trust-color' as string]: view.color }}
  >
    <div className={classes['trust-page__level-header']}>
      <strong>{view.label}</strong>
      <span>{view.shortLabel}</span>
    </div>
    <small>от {level.score} баллов</small>
    <p>{level.text}</p>
    <ul className={classes['trust-page__list']}>
      <li>{level.daily}</li>
      <li>Заявки: {level.tiers}</li>
      <li>{level.requirement}</li>
    </ul>
  </div>
  
);
    })}
  </div>
</section>

      <section className={classes['trust-page__card']}>
        <h2>Последние изменения</h2>
        {data.recent_events?.length ? (
          <ul className={classes['trust-page__events']}>
            {data.recent_events.map((event) => (
              <li key={event.id}>
                <div>
                  <span>{eventLabel(event)}</span>
                  {event.created_at && (
                    <small>{new Date(event.created_at).toLocaleDateString('ru-RU')}</small>
                  )}
                </div>
                <strong>
                  {event.delta > 0 ? '+' : ''}
                  {event.delta}
                </strong>
              </li>
            ))}
          </ul>
        ) : (
          <p>
            Пока нет событий рейтинга. Они появятся после заполнения профиля, проверки паспорта
            и первых занятий.
          </p>
        )}
      </section>
    </main>
  );
};
